import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import {createRef} from 'react';

import * as MultiSelectListUtil from 'notebook/mediocre/selection/block/MultiSelectListUtil';

export default {
  propTypes: {
    storeState: PropTypes.func,
    restoreState: PropTypes.func,
    metadataForRestore: PropTypes.object,
    multiEditing: PropTypes.bool, // specifies if the component is being multi-edited
  },

  getInitialState() {
    return {
      multiStartIndex: null, // anchor index / index selection started on
      multiEndIndex: null, // index selection ended on
      multiActive: null, // null if selection is not being updated
      multiSelIndex: null, // currently selected index within the selection
      multiActualIndex: null, // stateful index to keep track during navigation (could be in a merged cell)
      multiExtra: {}, // any state that should be tied to the selection's existence
    };
  },

  UNSAFE_componentWillMount() {
    const requiredMethods = [
      'previousIndex',
      'nextIndex',
      'firstIndex',
      'lastIndex',
      'compareIndex',
      'multiSelectKey',
      'isIndexBetween',
      'multiKeyDownHandler',
      'multiInputHandler',
      'multiPasteHandler',
      'multiGetHTMLForCopy',
      'isLockedWithEditWarn',
    ];

    // A component may also implement:
    //   multiGetTextForCopy: Returns text data to put on the clipboard.
    //   multiFinalizeEditHandler: A hook to handle when an item is being deselected/blurred.
    //   multiCrossEditing(): Returns true if we are actively updating a different multiselect.
    //     and that this component should multiTryInsert
    //   multiTryInsert({index, cid, cursor, shift}):
    //     Attempts to insert a cell reference at index into the current formula at position
    //     cursor with shift to indicate if it should expand/set the range. Returns true,
    //     if the insert was successful.
    //   multiCanInsert({start, end, cid, shift}):
    //     Determines if multiTryInsert would return true without making any modifications
    //   setMultiEditing({editing: bool, text: string}):
    //     Set the non-finalized text for the item that is being multi-edited
    //       (item with index @state.multiSelIndex when @props.multiEditing).
    //     Pass {editing: false} to exit multiEditing mode.
    //   multiNamespace: the data-namespace on elements that informs window mousedown to allow a cross-insert
    for (const method of Array.from(requiredMethods)) {
      if (this[method] == null) {
        throw new Error(`${this.constructor.displayName} must implement ${method}.`);
      }
    }
    MultiSelectListUtil.multiSelectableWillMount(
      this.props.metadataForRestore,
      this.multiSelectKey(),
      this.setState.bind(this)
    );
    this.focusBearerRef = createRef();
  },

  componentDidMount() {
    MultiSelectListUtil.multiSelectableDidMount(
      this._multiWindowMouseDownHandler,
      this._multiWindowMouseUpHandler
    );
  },

  componentWillUnmount() {
    MultiSelectListUtil.multiSelectableWillUnmount(
      this._multiWindowMouseDownHandler,
      this._multiWindowMouseUpHandler
    );
  },

  _multiWindowMouseDownHandler(e) {
    const options = {
      multiEditing: this.props.multiEditing,
      multiCanInsert: this.multiCanInsert,
      multiNamespace: this.multiNamespace ? this.multiNamespace() : undefined,
      multiFinalizeEditHandler: this.multiFinalizeEditHandler,
      domNode: ReactDOM.findDOMNode(this),
      multiStartIndex: this.state.multiStartIndex,
      multiEndIndex: this.state.multiEndIndex,
      setMultiEditing: this.setMultiEditing,
      setState: this.setState.bind(this),
      multiActive: this.state.multiActive,
      multiCrossEditing: this.multiCrossEditing ? this.multiCrossEditing() : false,
    };
    MultiSelectListUtil.multiWindowMouseDownHandler(e, options);
  },

  _multiWindowMouseUpHandler(e) {
    const options = {
      multiEditing: this.props.multiEditing,
      multiCanInsert: this.multiCanInsert,
      multiNamespace: this.multiNamespace ? this.multiNamespace() : undefined,
      multiFinalizeEditHandler: this.multiFinalizeEditHandler,
      domNode: ReactDOM.findDOMNode(this),
      multiStartIndex: this.state.multiStartIndex,
      multiEndIndex: this.state.multiEndIndex,
      setMultiEditing: this.setMultiEditing,
      setState: this.setState.bind(this),
      multiActive: this.state.multiActive,
      multiCrossEditing: this.multiCrossEditing ? this.multiCrossEditing() : false,
    };
    MultiSelectListUtil.multiWindowMouseUpHandler(e, options);
  },

  UNSAFE_componentWillReceiveProps(nextProps) {
    MultiSelectListUtil.multiSelectableWillReceiveProps(
      this.props,
      nextProps,
      this.multiSelectKey(),
      this.setState.bind(this)
    );
  },

  componentDidUpdate() {
    const options = {
      domNode: ReactDOM.findDOMNode(this),
      multiEditing: this.props.multiEditing,
      improperFocusHook: this.improperFocusHook,
      multiStartIndex: this.state.multiStartIndex,
      multiEndIndex: this.state.multiEndIndex,
      focusBearerNode: this.focusBearerRef.current,
    };
    MultiSelectListUtil.multiSelectableDidUpdate(options);
  },

  // # Component-callable helper methods

  sortedMultiRange() {
    return MultiSelectListUtil.sortedMultiRange(
      this.state.multiStartIndex,
      this.state.multiEndIndex,
      this.compareIndex
    );
  },

  // Takes the index of the note part with the current selection (could be null if multi-select).
  // If there is a multi-select, returns the multi-range.
  // Otherwise, returns a range containing just the passed in index.
  getSelectedRange(index) {
    return MultiSelectListUtil.getSelectedRange(index, {
      multiStartIndex: this.state.multiStartIndex,
      multiEndIndex: this.state.multiEndIndex,
      compareIndex: this.compareIndex,
    });
  },

  saveMultiSelect(state) {
    if (state == null) {
      ({state} = this);
    }
    MultiSelectListUtil.saveMultiSelect(state, this.props.storeState, this.multiSelectKey());
  },

  clearMultiSelect() {
    MultiSelectListUtil.clearMultiSelect(this.setState.bind(this));
  },

  setMultiExtra(multiExtraUpdate) {
    MultiSelectListUtil.setMultiExtra(multiExtraUpdate, this.setState.bind(this), this.state.multiExtra);
  },

  multiSelectSingle(index, param) {
    const options = {
      setState: this.setState.bind(this),
      multiStartIndex: this.state.multiStartIndex,
      multiEndIndex: this.state.multiEndIndex,
      setMultiEditing: this.setMultiEditing,
      getMultiEditingText: this.getMultiEditingText,
      getMultiEditingTypedValue: this.getMultiEditingTypedValue,
      focusBearerNode: this.focusBearerRef.current,
    };
    MultiSelectListUtil.multiSelectSingle(index, param, options);
  },

  multiCancelEdit(index) {
    const options = {
      setMultiEditing: this.setMultiEditing,
      getMultiEditingText: this.getMultiEditingText,
      getMultiEditingTypedValue: this.getMultiEditingTypedValue,
      focusBearerNode: this.focusBearerRef.current,
    };
    MultiSelectListUtil.multiCancelEdit(index, options);
  },

  setMultiIndices({start, end, text}) {
    const options = {
      setState: this.setState.bind(this),
      setMultiEditing: this.setMultiEditing,
      getMultiEditingText: this.getMultiEditingText,
      getMultiEditingTypedValue: this.getMultiEditingTypedValue,
      focusBearerNode: this.focusBearerRef.current,
    };
    MultiSelectListUtil.setMultiIndices({start, end, text}, options);
  },

  isSelected(index) {
    return MultiSelectListUtil.isSelected(
      index,
      this.state.multiStartIndex,
      this.state.multiEndIndex,
      this.isIndexBetween
    );
  },

  hasMultiSelection() {
    return MultiSelectListUtil.hasMultiSelection(this.state.multiStartIndex, this.state.multiEndIndex);
  },

  // # Component-registered handlers to manage multiselect state.

  multiEditableMouseDownHandler(args, e) {
    const options = {
      multiStartIndex: this.state.multiStartIndex,
      multiEndIndex: this.state.multiEndIndex,
      multiSelIndex: this.state.multiSelIndex,
      setState: this.setState.bind(this),
      allowSingleMultiSelect: this.allowSingleMultiSelect(),
      multiTryInsert: this.multiTryInsert,
      multiCrossEditing: this.multiCrossEditing ? this.multiCrossEditing() : false,
      multiFinalizeEditHandler: this.multiFinalizeEditHandler,
      isIndexBetween: this.isIndexBetween,
      compareIndex: this.compareIndex,
      setMultiEditing: this.setMultiEditing,
      getMultiEditingText: this.getMultiEditingText,
      getMultiEditingTypedValue: this.getMultiEditingTypedValue,
      focusBearerNode: this.focusBearerRef.current,
      disallowShiftExpand: false,
    };
    MultiSelectListUtil.multiEditableMouseDownHandler(args, e, options);
  },

  multiEditableMouseOverHandler(index) {
    const options = {
      setState: this.setState.bind(this),
      multiActive: this.state.multiActive,
      multiStartIndex: this.state.multiStartIndex,
      multiEndIndex: this.state.multiEndIndex,
      multiExtra: this.state.multiExtra,
      disallowMultiExpand: this.props.disallowMultiExpand ? this.props.disallowMultiExpand() : false,
      multiCrossEditing: this.multiCrossEditing ? this.multiCrossEditing() : false,
      allowSingleMultiSelect: this.allowSingleMultiSelect,
      multiTryInsert: this.multiTryInsert,
      compareIndex: this.compareIndex,
      focusBearerNode: this.focusBearerRef.current,
    };
    MultiSelectListUtil.multiEditableMouseOverHandler(index, options);
  },

  multiEditableMouseMoveHandler(index, e) {
    const options = {
      setState: this.setState.bind(this),
      multiActive: this.state.multiActive,
      multiStartIndex: this.state.multiStartIndex,
      multiEndIndex: this.state.multiEndIndex,
      multiExtra: this.state.multiExtra,
      compareIndex: this.compareIndex,
    };
    MultiSelectListUtil.multiEditableMouseMoveHandler(index, e, options);
  },

  computeState(state) {
    return MultiSelectListUtil.computeState(state, this.multiSelectKey());
  },

  renderFocusBearer() {
    const options = {
      state: this.state,
      isLockedWithEditWarn: () => this.isLockedWithEditWarn(),
      restoreState: this.props.restoreState,
      setState: (...rest) => this.setState(...rest),
      setMultiEditing: this.setMultiEditing,
      getMultiEditingText: this.getMultiEditingText,
      getMultiEditingTypedValue: this.getMultiEditingTypedValue,
      firstIndex: this.firstIndex(),
      lastIndex: this.lastIndex(),
      multiSelectable: this,
      multiSelectKey: this.multiSelectKey(),
      multiSelIndex: this.state.multiSelIndex,
      multiKeyDownHandler: this.multiKeyDownHandler,
      multiInputHandler: this.multiInputHandler,
      multiPasteHandler: this.multiPasteHandler,
      multiGetHTMLForCopy: this.multiGetHTMLForCopy,
      multiGetTextForCopy: this.multiGetTextForCopy,
      ref: this.focusBearerRef,
    };
    return MultiSelectListUtil.renderFocusBearer(options);
  },
};
